import React, { useState, useEffect } from 'react';
import ManualRoutingSteps from '../components/ManualRoutingSteps';
import AssignTechnicianStep from '../components/AssignTechnicianStep';
import ConfirmRoutesStep from '../components/ConfirmRoutesStep';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { SecurityBoundary, PermissionBoundary } from '@/lib/security';
import { PermissionScope } from '@/types';
import { logDataAccess } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import { useCurrentTenantId } from '@/contexts/TenantContext';

/* eslint-disable @typescript-eslint/no-explicit-any */
const ManualRoutingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const tenantId = useCurrentTenantId();

  const { selectedRecalls } = useSelectedRecalls();

  const { assignments, refreshData } = useTechnicianAssignments();

  // Auto-refresh assignments while on step 1 to detect when batch assignments complete
  useEffect(() => {
    if (currentStep === 1) {
      const interval = setInterval(() => {
        refreshData();
      }, 2000); // Refresh every 2 seconds while on assignment step

      return () => clearInterval(interval);
    }
  }, [currentStep, refreshData]);

  // Calculate unconfirmed recalls (same logic as AssignTechnicianStep)
  // Exclude recalls that have been processed (confirmed, completed, or cancelled)
  const unconfirmedRecalls = selectedRecalls.filter(recall => {
    const existingAssignment = assignments.find(
      a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
    );

    if (!existingAssignment) {
      return true;
    }

    const status = (existingAssignment as any).assignment_status;
    // Exclude: confirmed, completed, cancelled (these have been processed)
    // Include: assigned, in_progress (these can still be reassigned)
    return status !== 'confirmed' && status !== 'completed' && status !== 'cancelled';
  });

  // Calculate unconfirmed assignments only for selected recalls
  const unconfirmedAssignments = assignments.filter(assignment => {
    const isForSelectedRecall = selectedRecalls.some(
      recall =>
        recall.vin === assignment.vin && recall.campaign_number === assignment.campaign_number
    );
    const status = (assignment as any).assignment_status;
    // Exclude: confirmed, completed, cancelled (these have been processed)
    const isProcessed = status === 'confirmed' || status === 'completed' || status === 'cancelled';
    return isForSelectedRecall && !isProcessed;
  }).length;

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    // Refresh assignments data when changing steps to ensure we have latest data
    refreshData();
  };

  // Audit logging: Log data access when assignments are loaded
  useEffect(() => {
    if (assignments.length > 0 && tenantId) {
      logDataAccess({
        resource: 'technician_assignments',
        action: 'view',
        resourceCount: assignments.length,
        metadata: {
          step: currentStep,
          unconfirmedAssignments,
          totalRecalls: selectedRecalls.length,
          tenantId
        }
      });
    }
    // Only log once per assignments change to avoid spam
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments.length, currentStep]);

  // Log page view on mount
  useEffect(() => {
    logUserActivity({
      category: 'routing',
      action: 'view_manual_routing'
    });
  }, []);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <AssignTechnicianStep />;
      case 2:
        return <ConfirmRoutesStep />;
      default:
        return <AssignTechnicianStep />;
    }
  };

  return (
    <SecurityBoundary
      requiredPermissions={[
        { resource: 'recalls', action: 'assign_technicians', scope: PermissionScope.SELF }
      ]}
      auditPageAccess='manual-routing'
      tenantIsolation={true}
    >
      <div className='container mx-auto h-full'>
        <PermissionBoundary
          require={{
            resource: 'recalls',
            action: 'assign_technicians',
            scope: PermissionScope.SELF
          }}
          fallback={
            <div className='flex items-center justify-center h-full'>
              <div className='text-center p-8'>
                <p className='text-muted-foreground'>
                  You do not have permission to assign technicians to recalls.
                </p>
              </div>
            </div>
          }
        >
          <ManualRoutingSteps
            currentStep={currentStep}
            onStepChange={handleStepChange}
            totalAssignments={unconfirmedAssignments}
            totalRecalls={unconfirmedRecalls.length}
          >
            {renderStepContent()}
          </ManualRoutingSteps>
        </PermissionBoundary>
      </div>
    </SecurityBoundary>
  );
};

export default ManualRoutingPage;
